export type NavLink = {
  href: string;
  label: string;
};

export type ProductSummary = {
  id: "secure-ai" | "rapid-factory" | "kyber-docs" | "odan" | "mawforge";
  title: string;
  route: string;
  summary: string;
  capabilities: string[];
  statusLabel?: string;
};

export type SiteSearchDocument = {
  id: string;
  path: string;
  title: string;
  summary: string;
  category: "overview" | "solution" | "product" | "company" | "contact" | "news" | "careers";
  keywords: string[];
};

const mawforgeHref = "https://mawforge.ai";

export const primaryNavigation: NavLink[] = [
  { href: "/", label: "Home" },
  { href: "/solutions", label: "Solutions" },
  { href: "/who-we-are", label: "Company" },
  { href: "/news", label: "Insights" },
  { href: "/contact", label: "Contact" }
];

export const solutionNavigation: NavLink[] = [
  { href: "/solutions", label: "Solutions" },
  { href: "/solutions#secure-ai", label: "Secure AI" },
  { href: "/rapid-factory", label: "RapidFactory" },
  { href: "/kyber-docs", label: "KyberDocs" },
  { href: "/solutions#odan", label: "ODAN" },
  { href: "/solutions#mawforge", label: "Mawforge" },
  { href: "/control-plane", label: "Architecture" }
];

export const footerNavigation = {
  quickLinks: [
    { href: "/", label: "Home" },
    { href: "/solutions", label: "Solutions" },
    { href: "/solutions#platform", label: "Products" },
    { href: "/control-plane", label: "Architecture" },
    { href: "/who-we-are", label: "Company" },
    { href: "/news", label: "Insights" }
  ],
  contact: [
    { href: "/contact", label: "Contact Holocron" },
    { href: "mailto:info@holocronsecurity.com", label: "info@holocronsecurity.com" }
  ]
} as const;

export const productCatalog: ProductSummary[] = [
  {
    id: "secure-ai",
    title: "Secure AI Implementation",
    route: "/solutions#secure-ai",
    summary:
      "Secure AI Implementation helps teams design, govern, deploy, and operate AI workflows without losing policy control.",
    capabilities: [
      "Workflow and data-boundary design",
      "AI governance, review, and approval paths",
      "Deployment readiness for regulated environments"
    ]
  },
  {
    id: "rapid-factory",
    title: "RapidFactory",
    route: "/rapid-factory",
    summary:
      "RapidFactory builds and maintains AWS-first regulated environments with repeatable baselines and drift-aware workflows.",
    capabilities: [
      "CMMC Level 2 environment delivery support",
      "Repeatable infrastructure baselines",
      "Drift detection and remediation workflows"
    ]
  },
  {
    id: "kyber-docs",
    title: "KyberDocs",
    route: "/kyber-docs",
    summary:
      "KyberDocs supports evidence, endpoint remediation, and audit-readiness workflows for regulated operations.",
    capabilities: [
      "CKLB and SCAP evidence workflows",
      "Signed remediation job support",
      "Assessor-facing evidence presentation"
    ]
  },
  {
    id: "odan",
    title: "ODAN",
    route: "/solutions#odan",
    summary:
      "ODAN is the omnidirectional decision and authority network for seeing policy, risk, evidence, and action in one operating view.",
    capabilities: [
      "Authority-aware operational visibility",
      "Decision context for AI-enabled workflows",
      "Cross-product signal correlation"
    ]
  },
  {
    id: "mawforge",
    title: "Mawforge",
    route: "/solutions#mawforge",
    summary:
      "Mawforge supports large-scale security engineering and automation work where infrastructure, agents, and evidence need room to expand.",
    capabilities: [
      "Security automation build patterns",
      "Agent and workflow experimentation space",
      "Scalable delivery support for complex environments"
    ]
  }
];

export const siteSearchDocuments: SiteSearchDocument[] = [
  {
    id: "home",
    path: "/",
    title: "Holocron Security",
    summary:
      "Holocron helps DIB and regulated organizations implement AI, build compliant environments, and operate with machine-speed, policy-bound defense.",
    category: "overview",
    keywords: [
      "Conscious Security",
      "secure AI implementation",
      "CMMC Level 2",
      "DIB",
      "machine-speed",
      "policy-bound",
      "RapidFactory",
      "KyberDocs",
      "ODAN",
      "Mawforge"
    ]
  },
  {
    id: "solutions",
    path: "/solutions",
    title: "Solutions",
    summary:
      "Conscious Security in practice through secure AI implementation, CMMC environments, and RapidFactory + KyberDocs operations.",
    category: "solution",
    keywords: [
      "solutions",
      "secure AI implementation",
      "AI services",
      "CMMC Level 2",
      "regulated environment",
      "DIB",
      "ODAN",
      "Mawforge"
    ]
  },
  {
    id: "control-plane",
    path: "/control-plane",
    title: "Control Plane",
    summary:
      "The authority architecture behind Conscious Security for machine-speed, policy-bound defense and operations.",
    category: "overview",
    keywords: ["control plane", "Conscious Security", "policy-bound", "evidence", "authority", "operations"]
  },
  {
    id: "rapid-factory",
    path: "/rapid-factory",
    title: "RapidFactory",
    summary:
      "Compliant environment factory for AWS-first regulated workloads, CMMC Level 2 operations, and drift-aware delivery.",
    category: "product",
    keywords: [
      "RapidFactory",
      "CMMC Level 2",
      "regulated environments",
      "AWS",
      "drift detection",
      "remediation"
    ]
  },
  {
    id: "kyber-docs",
    path: "/kyber-docs",
    title: "KyberDocs",
    summary:
      "Evidence, endpoint remediation, and audit-readiness workflows for regulated operations.",
    category: "product",
    keywords: ["KyberDocs", "evidence", "CKLB", "SCAP", "audit", "remediation", "CMMC"]
  },
  {
    id: "who-we-are",
    path: "/who-we-are",
    title: "Who We Are",
    summary:
      "The Holocron team builds Conscious Security capabilities for secure AI, compliance, and regulated operations.",
    category: "company",
    keywords: ["mission", "team", "Conscious Security", "AI implementation", "compliance", "regulated operations"]
  },
  {
    id: "careers",
    path: "/careers",
    title: "Careers",
    summary:
      "Career opportunities for people building secure AI, evidence systems, and regulated operations infrastructure.",
    category: "careers",
    keywords: ["careers", "jobs", "team", "security", "AI", "compliance"]
  },
  {
    id: "news",
    path: "/news",
    title: "News & Events",
    summary:
      "Holocron thought leadership on Conscious Security, secure AI implementation, CMMC operations, and machine-speed defense.",
    category: "news",
    keywords: ["news", "events", "podcast", "media", "Conscious Security", "machine-speed cyber defense"]
  },
  {
    id: "rapid-factory-ownership",
    path: "/news/rapid-factory-ownership",
    title: "RAPID Factory Ownership Clarification",
    summary:
      "Official statement on ownership, patent filings, and attribution for RAPID Factory.",
    category: "news",
    keywords: ["ownership", "RAPID Factory", "patent", "statement", "attribution", "press"]
  },
  {
    id: "contact",
    path: "/contact",
    title: "Contact",
    summary:
      "Contact Holocron for secure AI implementation, CMMC Level 2 environments, or RapidFactory + KyberDocs demos.",
    category: "contact",
    keywords: ["contact", "secure AI implementation", "CMMC Level 2", "RapidFactory", "KyberDocs", "demo"]
  },
  {
    id: "odan",
    path: "/solutions#odan",
    title: "ODAN",
    summary:
      "Omnidirectional authority network for policy, risk, evidence, and action visibility across Holocron operations.",
    category: "product",
    keywords: ["ODAN", "authority", "visibility", "risk", "evidence", "AI operations"]
  },
  {
    id: "mawforge",
    path: "/solutions#mawforge",
    title: "Mawforge",
    summary:
      "Security engineering and automation forge for larger operating environments, agents, and infrastructure workflows.",
    category: "product",
    keywords: ["Mawforge", "automation", "security engineering", "agents", "infrastructure", "scale"]
  }
];
